import clsx from 'clsx';
import type { ClientVacationPeriod } from '../../../../../models/vacationClientView.types';
import type { ConflictPersonItem } from '../clientPeriodCard.types';
import type { ConflictCalendarModel } from '../../../vacationClientPanel.calendar';
import { PANEL_HEADER_HEIGHT, RULER_HEIGHT, ROW_HEIGHT, CALENDAR_TRACK_HEIGHT } from '../clientPeriodCard.definitions';
import { CALENDAR_BAR_CLASSES, barToneStyle } from '../../../vacationClientPanel.definitions';
import { formatPeriodRange } from '../../../vacationClientPanel.definitions';
import { ToneLegend } from './ToneLegend';

interface CalendarPanelProps {
    period: ClientVacationPeriod;
    people: ConflictPersonItem[];
    model: ConflictCalendarModel | null;
}

function barStyle(leftPct: number, widthPct: number, tone: string) {
    const position = { left: `${leftPct}%`, width: `${widthPct}%` };
    return { ...position, ...barToneStyle(tone) };
}

export function CalendarPanel({ period, people, model: calendarModel }: CalendarPanelProps) {

    if (!calendarModel || calendarModel.rows.length <= 1) {
        return <p className="text-sm text-raro-gray-500">Nenhuma sobreposição no calendário</p>;
    }

    const orderedRows = [...calendarModel.rows].sort((a, b) => {
        if (a.tone === 'self') return -1;
        if (b.tone === 'self') return 1;

        const aIndex = people.findIndex((p) => p.collaboratorId === a.collaboratorId);
        const bIndex = people.findIndex((p) => p.collaboratorId === b.collaboratorId);

        if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
        return aIndex !== -1 ? -1 : 1;
    });

    return (
        <div className="rounded-xl border border-primary-100 overflow-hidden">
            <div className={clsx('flex items-center justify-between px-4 bg-primary-50 border-b border-primary-100', PANEL_HEADER_HEIGHT)}>
                <h4 className="text-sm font-semibold text-primary-800">Visualização no calendário</h4>
                <ToneLegend people={people} model={calendarModel} />
            </div>

            <div className="overflow-x-auto">
                <div className="min-w-[600px]">
                    <div className={clsx('relative border-b border-primary-200 px-4', RULER_HEIGHT)}>
                        <div className="relative h-full flex flex-col">
                            <div className="relative flex-1">
                                {calendarModel.months.map((month) => (
                                    <div
                                        key={month.key}
                                        className="absolute top-0 h-full border-l border-primary-200 first:border-l-0"
                                        style={{ left: `${month.leftPct}%`, width: `${month.widthPct}%` }}
                                    >
                                        <div className="flex items-center h-full">
                                            <span className="text-[11px] font-semibold text-primary-600 pl-2 whitespace-nowrap leading-none">
                                                {month.label}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="relative flex-1 border-t border-primary-100">
                                {calendarModel.ticks.map((tick) => (
                                    <div
                                        key={tick.key}
                                        className="absolute top-0 h-full border-l border-primary-100 first:border-l-0"
                                        style={{ left: `${tick.leftPct}%` }}
                                    >
                                        <div className="flex items-center h-full">
                                            <span className="text-[10px] text-primary-500 pl-1 whitespace-nowrap leading-none">{tick.label}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {orderedRows.map((row) => (
                        <div key={row.id} className={clsx('flex flex-col justify-center border-b border-raro-gray-100 px-4', ROW_HEIGHT)}>
                            <span
                                className={clsx(
                                    'text-xs font-montserrat block mb-1',
                                    row.tone === 'self' ? 'font-semibold text-primary-900' : 'text-raro-gray-600'
                                )}
                                title={row.name}
                            >
                                {row.name}
                            </span>
                            <div className={clsx('relative rounded border', row.tone === 'self' ? 'bg-primary-50/60 border-primary-200' : 'bg-white border-raro-gray-200', CALENDAR_TRACK_HEIGHT)}>
                                {row.bars.map((bar) => (
                                    <span
                                        key={`${bar.start.getTime()}-${bar.end.getTime()}`}
                                        className={clsx(
                                            'absolute inset-y-0.5 rounded-full',
                                            CALENDAR_BAR_CLASSES[row.tone as keyof typeof CALENDAR_BAR_CLASSES]
                                        )}
                                        style={barStyle(bar.leftPct, bar.widthPct, row.tone)}
                                        title={formatPeriodRange(bar.start, bar.end)}
                                    />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
